import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { IChatItem } from '../../../../interfaces';
import { ChatService } from '../../../../services/chat.service';
import { TABS } from 'src/app/components/layout/chat-sidebar/components/chat/chat.interface';

@Component({
  selector: 'app-board-v3-link-chat',
  templateUrl: './link-chat.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class LinkChatComponent implements OnInit {
  @Input() excludes: string[] = [];
  @Output() close = new EventEmitter<{ chatId: string } | void>();

  private readonly chatService = inject(ChatService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchChanged = new Subject<string>();

  protected readonly take = 25;
  protected chats: IChatItem[] = [];
  protected search = '';
  protected loading = false;
  protected loadingMore = false;

  private page = 1;
  private hasMore = true;

  ngOnInit(): void {
    this.searchChanged.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
    this.reload();
  }

  protected onSearchChange(value: string): void {
    this.search = value;
    this.searchChanged.next(value);
  }

  protected onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight > 200) return;

    this.loadMore();
  }

  protected onSelectChat(chat: IChatItem): void {
    this.close.emit({ chatId: chat.id });
  }

  protected onCancel(): void {
    this.close.emit();
  }

  private reload(): void {
    this.page = 1;
    this.hasMore = true;
    this.loading = true;
    this.loadChats(true);
  }

  private loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) return;

    this.page += 1;
    this.loadingMore = true;
    this.loadChats(false);
  }

  private loadChats(reset: boolean): void {
    const params: Record<string, any> = { type: TABS.ACTIVE, page: this.page, take: this.take };
    if (this.search.trim()) params['search'] = this.search.trim();

    this.chatService
      .getChats(params, { excludes: this.excludes })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const items: IChatItem[] = response?.items ?? [];
          this.chats = reset ? items : [...this.chats, ...items];
          this.hasMore = this.chats.length < (response?.total ?? this.chats.length);
          this.loading = false;
          this.loadingMore = false;
        },
        error: () => {
          if (!reset) this.page -= 1;
          this.loading = false;
          this.loadingMore = false;
        },
      });
  }
}
